import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import SignaturePad from 'signature_pad';
import { useTranslation } from 'react-i18next';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { ConditionalPropertyPropType } from '../../propTypes';
import FieldTitle from '../../FieldTitle';
import FormHelperText from '../../FormHelperText';

const SignatureElement = forwardRef(function SignatureElement(props, ref) {
  const { t } = useTranslation(['icp-form-renderer']);

  const {
    keyPath,
    id,
    className: classNameProp,
    style,
    title,
    value,
    disabled,
    readonly,
    componentProps = {},
    fieldTitleProps,
    validation,
    helpers,
    onChange,
    onTouchChanged,
  } = props;

  const {
    penColor = 'black',
    minWidth = 0.5,
    maxWidth = 2.5,
    backgroundColor = 'rgba(0,0,0,0)',
    outputType = 'image/png',
    canvasWidth,
    canvasHeight = 200,
    hideToolbar = false,
    style: componentStyle,
    ...otherComponentProps
  } = componentProps;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const className = useClassName(classNameProp);

  const nodeRef = useRef(null);
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const isInternalChange = useRef(false);

  // 设置 canvas 物理尺寸（处理高 DPI 屏幕）
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = canvasWidth || canvas.offsetWidth || 400;
    canvas.width = width * ratio;
    canvas.height = canvasHeight * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
    // 按照官方文档，resize 后需要 clear
    if (padRef.current) {
      padRef.current.clear();
    }
  }, [canvasWidth, canvasHeight]);

  // 初始化 SignaturePad
  useEffect(() => {
    if (!canvasRef.current || isInDesign) return undefined;

    const pad = new SignaturePad(canvasRef.current, {
      penColor,
      minWidth,
      maxWidth,
      backgroundColor,
    });

    if (disabled || readonly) {
      pad.off();
    }

    pad.addEventListener('endStroke', () => {
      isInternalChange.current = true;
      const dataUrl = pad.toDataURL(outputType);
      onChange?.(dataUrl);
      onTouchChanged?.();
    });

    padRef.current = pad;

    return () => {
      pad.off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInDesign]);

  // 响应 disabled/readonly 变化
  useEffect(() => {
    if (!padRef.current) return;
    if (disabled || readonly) {
      padRef.current.off();
    } else {
      padRef.current.on();
    }
  }, [disabled, readonly]);

  // 外部 value 变化时回显签名
  useEffect(() => {
    if (!padRef.current) return;
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    padRef.current.clear();
    if (value) {
      // 默认 fromDataURL 会用 devicePixelRatio 计算正确的绘制尺寸
      padRef.current.fromDataURL(value);
    }
  }, [value]);

  const handleClear = useCallback(() => {
    if (padRef.current) {
      padRef.current.clear();
      onChange?.('');
      onTouchChanged?.();
    }
  }, [onChange, onTouchChanged]);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
      signaturePad: padRef.current,
      clear: handleClear,
    }),
    [handleClear],
  );

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'signature-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <div>
          {readonly && value && (
            <img src={value} alt={title || 'signature'} className="signature-element-preview" />
          )}
          {readonly && !value && (
            <span className="readonly-text">{t('signature.no-signature')}</span>
          )}
          {!readonly && (
            <div
              {...otherComponentProps}
              className={clsx('signature-element-canvas-wrapper', otherComponentProps.className)}
              style={componentStyle}
            >
              <canvas
                ref={canvasRef}
                className="signature-element-canvas"
                style={{ width: canvasWidth || undefined, height: canvasHeight }}
              />
              {!hideToolbar && (
                <div className="signature-element-toolbar">
                  <span className="signature-element-toolbar-hint">{t('signature.hint')}</span>
                  {!disabled && !isInDesign && (
                    <button
                      type="button"
                      className="signature-element-clear-btn"
                      onClick={handleClear}
                    >
                      {t('signature.clear')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <FormHelperText helpers={helpers} />
        </div>
      </div>
    </ElementDecorator>
  );
});

SignatureElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.string,
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
    /**
     * 笔触颜色
     * @default 'black'
     */
    penColor: PropTypes.string,
    /**
     * 笔触最小宽度
     * @default 0.5
     */
    minWidth: PropTypes.number,
    /**
     * 笔触最大宽度
     * @default 2.5
     */
    maxWidth: PropTypes.number,
    /**
     * 画布背景色
     * @default 'rgba(0,0,0,0)'
     */
    backgroundColor: PropTypes.string,
    /**
     * 输出格式
     * @default 'image/png'
     */
    outputType: PropTypes.oneOf(['image/png', 'image/jpeg', 'image/svg+xml']),
    /**
     * 画布宽度，默认跟随容器宽度
     */
    canvasWidth: PropTypes.number,
    /**
     * 画布高度
     * @default 200
     */
    canvasHeight: PropTypes.number,
    /**
     * 是否隐藏底部工具栏
     * @default false
     */
    hideToolbar: PropTypes.bool,
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
SignatureElement.displayName = 'Signature';

export default withFieldWrapper(SignatureElement);
